import "./Hero.css";
import { useRef, useState, useEffect } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import video1 from "../../assets/herovideo.webm";
import posterImg from "../../assets/herovideo-poster.jpg";
import ScrollTrigger from "gsap/ScrollTrigger";
import { useContextAwareness } from "../../hooks/useContextAwareness";
import { scrollToTarget } from "../../utils/scroll";
import { openBooking } from "../../utils/booking";

gsap.registerPlugin(useGSAP, ScrollTrigger);

const NAV = [
  { label: "Home", target: ".hero" },
  { label: "Work", target: ".casestudies" },
  { label: "About", target: ".about" },
  { label: "Contact", target: ".footer" },
];

function Hero({ startAnimation }) {
  const container = useRef();
  const videoRef = useRef();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);
  const { greeting, welcomeText } = useContextAwareness();

  // Lazy: the poster (the video's first frame) shows until the preloader is
  // done; only then does the video start downloading, so it never competes
  // with first paint
  useEffect(() => {
    if (startAnimation) setVideoSrc(video1);
  }, [startAnimation]);

  const handleVideoLoad = () => {
    setVideoLoaded(true);
    videoRef.current?.play();
  };

  useGSAP(
    () => {
      // Hide before the preloader finishes to avoid a flash of text
      gsap.set(".hero-title", { y: 30, autoAlpha: 0, filter: "blur(10px)" });
      gsap.set(".hero-reveal", { y: 20, autoAlpha: 0 });

      if (!startAnimation) return;

      gsap
        .timeline({ defaults: { ease: "power2.out" } })
        .to(".hero-title", { y: 0, autoAlpha: 1, filter: "blur(0px)", duration: 0.8 }, 0.5)
        .to(".hero-reveal", { y: 0, autoAlpha: 1, duration: 0.8, stagger: 0.2 }, "-=0.4");

      // Scroll: frame zooms out and sinks (desktop) or drifts (mobile),
      // corner text fades.
      gsap.matchMedia().add(
        { isDesktop: "(min-width: 800px)", isMobile: "(max-width: 799px)" },
        ({ conditions: { isMobile } }) => {
          gsap
            .timeline({
              scrollTrigger: { trigger: container.current, start: "top top", end: "bottom top", scrub: true },
            })
            .fromTo(
              ".hero-frame",
              { scale: 1, y: 0 },
              { scale: isMobile ? 1 : 0.8, y: isMobile ? 50 : 200, ease: "none" },
            )
            .to(
              ".hero-ui",
              isMobile
                ? { y: -50, autoAlpha: 0, ease: "none" }
                : { autoAlpha: 0, filter: "blur(10px)", ease: "none" },
              "<",
            );
        },
      );
    },
    { scope: container, dependencies: [startAnimation] },
  );

  return (
    <section className="hero" ref={container}>
      <div className="hero-frame">
        <div className="hero-media">
          <img src={posterImg} alt="" className="hero-poster-img" width="1920" height="1080" fetchpriority="high" />
          {videoSrc && (
            <video
              ref={videoRef}
              className={`hero-video ${videoLoaded ? "video-visible" : "video-hidden"}`}
              src={videoSrc}
              muted
              loop
              playsInline
              onLoadedData={handleVideoLoad}
            />
          )}
        </div>
        <div className="hero-shade" />

        <div className="hero-ui">
          <div className="hero-top">
          </div>

          <h1 className="hero-title">
            <span>ENGINEERING<br />INTERFACES</span>
            <span style={{ textAlign: "right" }}>THAT<br /><em className="accent">Think</em></span>
          </h1>

          <div className="hero-bottom">
            <div className="hero-col hero-reveal">
              <span className="hero-label">{welcomeText}</span>
              <p className="hero-value">
                {greeting}{" "}
                <button type="button" className="hero-cta" onClick={() => openBooking()}>
                  Let's build together
                </button>
                {" or "}
                <button type="button" className="hero-cta" onClick={() => scrollToTarget(".spectrum")}>
                  see packages
                </button>
              </p>
            </div>
            <div className="hero-col hero-reveal">
              <span className="hero-label">Role</span>
              <p className="hero-value">Design Engineer for <em className="accent">Human-Centered</em> AI Products</p>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
